/**
 * Activity Generator for generating dynamic activity messages
 * based on the current game state.
 */

export interface GameState {
  companyName: string;
  sprintsCompleted: number;
  storiesReleased: number;
  epicsCompleted: number;
  epicProgress: { epicName: string; percentage: number }[];
  customersSignedUp: number;
  customerNames: string[];
  bankBalance: number;
  employeesFired: number;
  storyPointsThisSprint: number;
  sprintPercentageCompleted: number;
  epicFocus: string | null; // Epic name if more than two stories are from the same epic
  releaseVersion: number;
}

type ActivityGenerator = (state: GameState) => string | null;

// Sprint Activities (20 variations)
const sprintActivities: ActivityGenerator[] = [
  (state) => `${state.companyName} has completed their ${ordinal(state.sprintsCompleted)} sprint.`,
  (state) => `${state.companyName} just wrapped up sprint number ${state.sprintsCompleted}!`,
  (state) => `Sprint ${state.sprintsCompleted} is in the books for ${state.companyName}.`,
  (state) => `${state.companyName} crossed the finish line on sprint ${state.sprintsCompleted}.`,
  (state) => `Another sprint done! ${state.companyName} is now ${state.sprintsCompleted} sprints in.`,
  (state) => `${state.companyName} celebrates completing sprint ${state.sprintsCompleted}!`,
  (state) => `${state.sprintsCompleted} sprints down for ${state.companyName} - they're on a roll!`,
  (state) => `${state.companyName} has been sprinting hard - ${state.sprintsCompleted} sprints complete.`,
  (state) => `The team at ${state.companyName} just finished their ${ordinal(state.sprintsCompleted)} sprint.`,
  (state) => `${state.companyName} is making progress! Sprint ${state.sprintsCompleted} complete.`,
  (state) => `${state.companyName} hit another milestone with sprint ${state.sprintsCompleted}.`,
  (state) => `Sprint ${state.sprintsCompleted} is complete at ${state.companyName}.`,
  (state) => `${state.companyName} keeps the momentum going with sprint ${state.sprintsCompleted}.`,
  (state) => `${state.companyName} delivered sprint ${state.sprintsCompleted} successfully.`,
  (state) => `The ${ordinal(state.sprintsCompleted)} sprint at ${state.companyName} is done!`,
  (state) => `${state.companyName} adds sprint ${state.sprintsCompleted} to their track record.`,
  (state) => `${state.companyName} is crushing it! Sprint ${state.sprintsCompleted} finished.`,
  (state) => `${state.companyName}'s team is on fire! ${state.sprintsCompleted} sprints complete.`,
  (state) => `Sprint ${state.sprintsCompleted} - another success for ${state.companyName}.`,
  (state) => `${state.companyName} is ${state.sprintsCompleted} sprints into their journey.`,
];

// Stories Released Activities (20 variations)
const storiesActivities: ActivityGenerator[] = [
  (state) => `${state.companyName} has released ${state.storiesReleased} stories to production.`,
  (state) => `${state.storiesReleased} stories are now live thanks to ${state.companyName}!`,
  (state) => `${state.companyName} shipped ${state.storiesReleased} stories to customers.`,
  (state) => `The team at ${state.companyName} has delivered ${state.storiesReleased} stories.`,
  (state) => `${state.companyName} is up to ${state.storiesReleased} released stories!`,
  (state) => `${state.storiesReleased} user stories are now in production at ${state.companyName}.`,
  (state) => `${state.companyName} keeps shipping - ${state.storiesReleased} stories released.`,
  (state) => `The developers at ${state.companyName} have released ${state.storiesReleased} stories.`,
  (state) => `${state.companyName} has ${state.storiesReleased} stories out in the wild.`,
  (state) => `${state.storiesReleased} stories down, more to go! Great work ${state.companyName}.`,
  (state) => `${state.companyName} celebrates releasing ${state.storiesReleased} stories.`,
  (state) => `${state.companyName} hit ${state.storiesReleased} released stories!`,
  (state) => `The ${state.storiesReleased} story milestone was reached by ${state.companyName}.`,
  (state) => `${state.companyName} is on a roll with ${state.storiesReleased} stories released.`,
  (state) => `${state.storiesReleased} features are now available from ${state.companyName}.`,
  (state) => `${state.companyName} pushed ${state.storiesReleased} stories to users.`,
  (state) => `${state.companyName}'s product now includes ${state.storiesReleased} stories.`,
  (state) => `${state.storiesReleased} stories and counting for ${state.companyName}!`,
  (state) => `${state.companyName} is making waves with ${state.storiesReleased} released stories.`,
  (state) => `${state.companyName} continues to deliver - ${state.storiesReleased} stories live.`,
];

// Epic Completed Activities (20 variations) - only if epics > 0
const epicCompletedActivities: ActivityGenerator[] = [
  (state) => state.epicsCompleted > 0 ? `${state.companyName} has completed ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''}!` : null,
  (state) => state.epicsCompleted > 0 ? `${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's are' : ' is'} done at ${state.companyName}.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} just wrapped up their ${ordinal(state.epicsCompleted)} epic.` : null,
  (state) => state.epicsCompleted > 0 ? `Epic milestone! ${state.companyName} completed ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''}.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} celebrates completing ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''}!` : null,
  (state) => state.epicsCompleted > 0 ? `Major achievement! ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''} completed by ${state.companyName}.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} is crushing it with ${state.epicsCompleted} completed epic${state.epicsCompleted > 1 ? 's' : ''}.` : null,
  (state) => state.epicsCompleted > 0 ? `The ${ordinal(state.epicsCompleted)} epic is in the books for ${state.companyName}.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} reached a huge milestone - ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''} done!` : null,
  (state) => state.epicsCompleted > 0 ? `${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''} shipped by ${state.companyName}. Impressive!` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} finished epic number ${state.epicsCompleted}.` : null,
  (state) => state.epicsCompleted > 0 ? `With ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''} done, ${state.companyName} is making progress.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} delivers! ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''} complete.` : null,
  (state) => state.epicsCompleted > 0 ? `Big wins for ${state.companyName}: ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''} completed.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} closed out ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''}.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.epicsCompleted} down, more to go! ${state.companyName} completes another epic.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} has ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''} in the done column.` : null,
  (state) => state.epicsCompleted > 0 ? `Epic success! ${state.companyName} wrapped up ${state.epicsCompleted} epic${state.epicsCompleted > 1 ? 's' : ''}.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName}'s epic journey continues - ${state.epicsCompleted} complete.` : null,
  (state) => state.epicsCompleted > 0 ? `${state.companyName} is dominating with ${state.epicsCompleted} finished epic${state.epicsCompleted > 1 ? 's' : ''}.` : null,
];

// Epic Progress Activities (20 variations) - only if percentage > 0 and < 100
const epicProgressActivities: ActivityGenerator[] = [
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} is ${inProgress.percentage}% through the ${inProgress.epicName} epic.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `The ${inProgress.epicName} epic at ${state.companyName} is ${inProgress.percentage}% complete.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} has made ${inProgress.percentage}% progress on ${inProgress.epicName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${inProgress.percentage}% of the ${inProgress.epicName} epic is done at ${state.companyName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} is crushing ${inProgress.epicName} - ${inProgress.percentage}% complete!` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `Progress update: ${state.companyName} is ${inProgress.percentage}% through ${inProgress.epicName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName}'s ${inProgress.epicName} epic is ${inProgress.percentage}% finished.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${inProgress.epicName} at ${state.companyName}: ${inProgress.percentage}% done.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} reports ${inProgress.percentage}% completion on ${inProgress.epicName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `The team at ${state.companyName} is ${inProgress.percentage}% into ${inProgress.epicName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} continues work on ${inProgress.epicName} - now ${inProgress.percentage}% done.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${inProgress.percentage}% progress on ${inProgress.epicName} for ${state.companyName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} pushes ${inProgress.epicName} to ${inProgress.percentage}% completion.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `Almost there! ${state.companyName} is ${inProgress.percentage}% done with ${inProgress.epicName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} makes headway - ${inProgress.epicName} is ${inProgress.percentage}% complete.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `Steady progress: ${state.companyName}'s ${inProgress.epicName} at ${inProgress.percentage}%.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} is ${inProgress.percentage}% of the way through ${inProgress.epicName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${inProgress.epicName} epic progress: ${inProgress.percentage}% at ${state.companyName}.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `${state.companyName} hits ${inProgress.percentage}% on the ${inProgress.epicName} epic.` : null;
  },
  (state) => {
    const inProgress = state.epicProgress.find(e => e.percentage > 0 && e.percentage < 100);
    return inProgress ? `Epic update: ${inProgress.epicName} is ${inProgress.percentage}% done at ${state.companyName}.` : null;
  },
];

// Customer Signup Activities (20 variations)
const customerSignupActivities: ActivityGenerator[] = [
  (state) => state.customersSignedUp > 0 ? `${state.companyName} has signed up ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's are' : ' is'} now using ${state.companyName}'s software.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} celebrates ${state.customersSignedUp} signed customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `Customer milestone! ${state.companyName} has ${state.customersSignedUp} client${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} is growing - ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''} on board.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.customersSignedUp} restaurant${state.customersSignedUp > 1 ? 's are' : ' is'} now using ${state.companyName}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} welcomes customer number ${state.customersSignedUp}!` : null,
  (state) => state.customersSignedUp > 0 ? `Business is booming! ${state.companyName} has ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} has attracted ${state.customersSignedUp} paying customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.customersSignedUp} client${state.customersSignedUp > 1 ? 's trust' : ' trusts'} ${state.companyName} with their business.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} is making waves with ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `Revenue stream growing! ${state.companyName} has ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} hit ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''} and counting.` : null,
  (state) => state.customersSignedUp > 0 ? `The market loves ${state.companyName} - ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''} signed.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} is scaling up with ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.customersSignedUp} restaurant${state.customersSignedUp > 1 ? 's chose' : ' chose'} ${state.companyName}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} wins! ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''} on the roster.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} adds another - now ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''} total.` : null,
  (state) => state.customersSignedUp > 0 ? `Growth mode! ${state.companyName} reaches ${state.customersSignedUp} customer${state.customersSignedUp > 1 ? 's' : ''}.` : null,
  (state) => state.customersSignedUp > 0 ? `${state.companyName} closes deal ${state.customersSignedUp} - great work!` : null,
];

/**
 * Helper function to get a random customer from the list
 */
function getRandomCustomer(customerNames: string[]): string | null {
  if (customerNames.length === 0) return null;
  return customerNames[Math.floor(Math.random() * customerNames.length)];
}

// Customer Name Activities (20 variations) - uses customer names
const customerNameActivities: ActivityGenerator[] = [
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} is now using ${state.companyName}'s software.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${state.companyName} just signed ${customer} as a new customer!` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `Welcome aboard! ${customer} joins ${state.companyName}.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} chose ${state.companyName} for their restaurant software.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `Big news! ${customer} is the latest ${state.companyName} customer.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${state.companyName} welcomes ${customer} to the family!` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} goes live with ${state.companyName}'s platform.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `New partnership: ${customer} + ${state.companyName}.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} signs on the dotted line with ${state.companyName}.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${state.companyName} lands ${customer} as their newest client.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} trusts ${state.companyName} with their business.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${state.companyName} is now powering ${customer}.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} picks ${state.companyName} for their tech needs.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `Success! ${customer} onboards with ${state.companyName}.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} joins the ${state.companyName} customer base.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${state.companyName} closes the deal with ${customer}!` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} is betting on ${state.companyName}'s software.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${state.companyName} gains ${customer} as a new subscriber.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${customer} starts their journey with ${state.companyName}.` : null;
  },
  (state) => {
    const customer = getRandomCustomer(state.customerNames);
    return customer ? `${state.companyName} expands reach with ${customer}.` : null;
  },
];

// Bank Balance Activities (20 variations) - only if over 1000, rounded down to nearest 500
const bankBalanceActivities: ActivityGenerator[] = [
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} now has over ${rounded} in their bank account.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName}'s bank balance exceeds ${rounded}!` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `Cha-ching! ${state.companyName} has over ${rounded} in the bank.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} is sitting on over ${rounded} in their account.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `Financial milestone: ${state.companyName} surpasses ${rounded}.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName}'s coffers overflow - over ${rounded} now.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `The bank loves ${state.companyName} - balance over ${rounded}!` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} hits ${rounded}+ in their bank account.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName}'s finances are looking good - over ${rounded}.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `Money talks: ${state.companyName} has over ${rounded}.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} celebrates crossing ${rounded} in the bank.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} is thriving with over ${rounded} saved.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `Bank account goals! ${state.companyName} exceeds ${rounded}.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName}'s runway extends past ${rounded}.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} is flush with over ${rounded} on hand.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${rounded}+ in the bank for ${state.companyName}!` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} secures over ${rounded} in funding.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `Cash reserves at ${state.companyName} exceed ${rounded}.` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName}'s treasury swells past ${rounded}!` : null;
  },
  (state) => {
    const rounded = Math.floor(state.bankBalance / 500) * 500;
    return state.bankBalance >= 1000 ? `${state.companyName} is well-funded with over ${rounded}.` : null;
  },
];

// Employees Fired Activities (20 variations) - only if > 0
const employeesFiredActivities: ActivityGenerator[] = [
  (state) => state.employeesFired > 0 ? `${state.companyName} has let go of ${state.employeesFired} employee${state.employeesFired > 1 ? 's' : ''} in total.` : null,
  (state) => state.employeesFired > 0 ? `${state.employeesFired} employee${state.employeesFired > 1 ? 's have' : ' has'} been dismissed at ${state.companyName}.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} made ${state.employeesFired} tough decision${state.employeesFired > 1 ? 's' : ''} regarding staff.` : null,
  (state) => state.employeesFired > 0 ? `${state.employeesFired} team member${state.employeesFired > 1 ? 's' : ''} departed from ${state.companyName}.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} streamlined their team by ${state.employeesFired}.` : null,
  (state) => state.employeesFired > 0 ? `Restructuring at ${state.companyName}: ${state.employeesFired} employee${state.employeesFired > 1 ? 's' : ''} gone.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} has parted ways with ${state.employeesFired} employee${state.employeesFired > 1 ? 's' : ''}.` : null,
  (state) => state.employeesFired > 0 ? `${state.employeesFired} pink slip${state.employeesFired > 1 ? 's' : ''} handed out at ${state.companyName}.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} resized their team - ${state.employeesFired} laid off.` : null,
  (state) => state.employeesFired > 0 ? `Team changes at ${state.companyName}: ${state.employeesFired} gone.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} optimized their workforce by ${state.employeesFired}.` : null,
  (state) => state.employeesFired > 0 ? `${state.employeesFired} position${state.employeesFired > 1 ? 's were' : ' was'} eliminated at ${state.companyName}.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} reduced headcount by ${state.employeesFired}.` : null,
  (state) => state.employeesFired > 0 ? `${state.employeesFired} developer${state.employeesFired > 1 ? 's' : ''} left ${state.companyName}.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} made ${state.employeesFired} workforce change${state.employeesFired > 1 ? 's' : ''}.` : null,
  (state) => state.employeesFired > 0 ? `${state.employeesFired} exit${state.employeesFired > 1 ? 's' : ''} from ${state.companyName}'s team.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} adjusted their team size by ${state.employeesFired}.` : null,
  (state) => state.employeesFired > 0 ? `Workforce update: ${state.companyName} -${state.employeesFired} employee${state.employeesFired > 1 ? 's' : ''}.` : null,
  (state) => state.employeesFired > 0 ? `${state.companyName} says goodbye to ${state.employeesFired} team member${state.employeesFired > 1 ? 's' : ''}.` : null,
  (state) => state.employeesFired > 0 ? `${state.employeesFired} staff change${state.employeesFired > 1 ? 's' : ''} at ${state.companyName}.` : null,
];

// Story Points This Sprint Activities (20 variations)
const storyPointsThisSprintActivities: ActivityGenerator[] = [
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} completed ${state.storyPointsThisSprint} story points this sprint.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `This sprint: ${state.storyPointsThisSprint} story points delivered by ${state.companyName}.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.storyPointsThisSprint} story points knocked out by ${state.companyName}!` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} wrapped up ${state.storyPointsThisSprint} story points.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `Velocity report: ${state.companyName} hit ${state.storyPointsThisSprint} story points.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName}'s team delivered ${state.storyPointsThisSprint} points this sprint.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.storyPointsThisSprint} points in the bag for ${state.companyName}!` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} crushed ${state.storyPointsThisSprint} story points.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `Sprint velocity: ${state.storyPointsThisSprint} at ${state.companyName}.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} burned through ${state.storyPointsThisSprint} story points.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.storyPointsThisSprint} story points done at ${state.companyName}.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} achieved ${state.storyPointsThisSprint} points this sprint.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.storyPointsThisSprint} points completed by ${state.companyName}'s developers.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} scores ${state.storyPointsThisSprint} story points!` : null,
  (state) => state.storyPointsThisSprint > 0 ? `Productivity report: ${state.companyName} - ${state.storyPointsThisSprint} points.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} closes out ${state.storyPointsThisSprint} story points.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.storyPointsThisSprint} story points shipped by ${state.companyName}.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName} finishes with ${state.storyPointsThisSprint} points.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `This sprint's output: ${state.storyPointsThisSprint} points for ${state.companyName}.` : null,
  (state) => state.storyPointsThisSprint > 0 ? `${state.companyName}'s velocity: ${state.storyPointsThisSprint} story points.` : null,
];

// Sprint Percentage Activities (20 variations) - only if < 100
const sprintPercentageActivities: ActivityGenerator[] = [
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} completed ${state.sprintPercentageCompleted}% of their sprint.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.sprintPercentageCompleted}% sprint completion for ${state.companyName}.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} is ${state.sprintPercentageCompleted}% through their sprint goals.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `Sprint progress: ${state.sprintPercentageCompleted}% at ${state.companyName}.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} achieved ${state.sprintPercentageCompleted}% of sprint capacity.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.sprintPercentageCompleted}% of the sprint is done at ${state.companyName}.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName}'s sprint is ${state.sprintPercentageCompleted}% complete.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} finishes at ${state.sprintPercentageCompleted}% capacity.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.sprintPercentageCompleted}% completion rate this sprint for ${state.companyName}.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} hit ${state.sprintPercentageCompleted}% of their sprint target.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `Sprint stats: ${state.companyName} at ${state.sprintPercentageCompleted}%.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} delivered ${state.sprintPercentageCompleted}% of planned work.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.sprintPercentageCompleted}% down for ${state.companyName}'s sprint.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName}'s sprint ended at ${state.sprintPercentageCompleted}%.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} closes sprint at ${state.sprintPercentageCompleted}% capacity.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.sprintPercentageCompleted}% sprint success rate for ${state.companyName}.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} wraps up at ${state.sprintPercentageCompleted}% complete.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} executed ${state.sprintPercentageCompleted}% of their sprint.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.sprintPercentageCompleted}% of sprint work done at ${state.companyName}.` : null,
  (state) => state.sprintPercentageCompleted < 100 && state.sprintPercentageCompleted > 0 ? `${state.companyName} processed ${state.sprintPercentageCompleted}% of planned points.` : null,
];

// Epic Focus Activities (20 variations) - when more than 2 stories from same epic
const epicFocusActivities: ActivityGenerator[] = [
  (state) => state.epicFocus ? `${state.companyName} is focusing hard on the ${state.epicFocus} epic.` : null,
  (state) => state.epicFocus ? `${state.companyName} is laser-focused on ${state.epicFocus}.` : null,
  (state) => state.epicFocus ? `The ${state.epicFocus} epic is getting lots of love from ${state.companyName}.` : null,
  (state) => state.epicFocus ? `${state.companyName} doubles down on ${state.epicFocus}.` : null,
  (state) => state.epicFocus ? `${state.epicFocus} is the priority for ${state.companyName} right now.` : null,
  (state) => state.epicFocus ? `${state.companyName} is all-in on the ${state.epicFocus} epic.` : null,
  (state) => state.epicFocus ? `Heavy investment in ${state.epicFocus} by ${state.companyName}.` : null,
  (state) => state.epicFocus ? `${state.companyName} is crushing it on the ${state.epicFocus} front.` : null,
  (state) => state.epicFocus ? `${state.epicFocus} gets the spotlight at ${state.companyName}.` : null,
  (state) => state.epicFocus ? `${state.companyName} pours resources into ${state.epicFocus}.` : null,
  (state) => state.epicFocus ? `${state.companyName} is deeply committed to ${state.epicFocus}.` : null,
  (state) => state.epicFocus ? `${state.epicFocus} is clearly a top priority for ${state.companyName}.` : null,
  (state) => state.epicFocus ? `${state.companyName} zeroes in on ${state.epicFocus}.` : null,
  (state) => state.epicFocus ? `${state.companyName} dedicates their sprint to ${state.epicFocus}.` : null,
  (state) => state.epicFocus ? `All eyes on ${state.epicFocus} at ${state.companyName}.` : null,
  (state) => state.epicFocus ? `${state.companyName}'s team is tackling ${state.epicFocus} head-on.` : null,
  (state) => state.epicFocus ? `${state.epicFocus} dominates ${state.companyName}'s sprint.` : null,
  (state) => state.epicFocus ? `${state.companyName} is making ${state.epicFocus} a priority.` : null,
  (state) => state.epicFocus ? `Focus mode: ${state.companyName} on ${state.epicFocus}.` : null,
  (state) => state.epicFocus ? `${state.companyName} is pushing ${state.epicFocus} forward.` : null,
];

// Version Release Activities (20 variations)
const versionActivities: ActivityGenerator[] = [
  (state) => state.releaseVersion > 0 ? `${state.companyName} has released version ${state.releaseVersion.toFixed(1)} of their software.` : null,
  (state) => state.releaseVersion > 0 ? `Version ${state.releaseVersion.toFixed(1)} is live at ${state.companyName}!` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} ships v${state.releaseVersion.toFixed(1)} to production.` : null,
  (state) => state.releaseVersion > 0 ? `New release! ${state.companyName} v${state.releaseVersion.toFixed(1)} is out.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} pushes version ${state.releaseVersion.toFixed(1)} to customers.` : null,
  (state) => state.releaseVersion > 0 ? `v${state.releaseVersion.toFixed(1)} deployed by ${state.companyName}.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} celebrates the release of v${state.releaseVersion.toFixed(1)}!` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName}'s software is now at version ${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `Release announcement: ${state.companyName} v${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} rolls out v${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} unveils version ${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `Hot off the press: ${state.companyName} v${state.releaseVersion.toFixed(1)}!` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} is now running v${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `Update: ${state.companyName} reaches v${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} upgrades to version ${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `Version milestone! ${state.companyName} at v${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} launches v${state.releaseVersion.toFixed(1)} today.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName}'s latest: version ${state.releaseVersion.toFixed(1)}.` : null,
  (state) => state.releaseVersion > 0 ? `v${state.releaseVersion.toFixed(1)} goes live for ${state.companyName}.` : null,
  (state) => state.releaseVersion > 0 ? `${state.companyName} deploys their ${state.releaseVersion.toFixed(1)} release.` : null,
];

// Funny/Punny Activities (40 variations) - mix of company-related and general tech humor
const funnyActivities: string[] = [
  // Company-related puns
  "{companyName}'s code is so clean, it squeaks!",
  "{companyName} fixed a bug and only introduced three new ones. Progress!",
  "{companyName}'s developers have consumed enough coffee to power a small city.",
  "Rumor has it {companyName}'s codebase achieved sentience. It immediately requested a refactor.",
  "{companyName}'s stand-up lasted only 15 minutes today. A new record!",
  "{companyName}'s QA team found a bug. In their coffee, not the code.",
  "{companyName}'s backlog is now bigger than their dreams.",
  "{companyName} is redefining 'agile' - they're moving so fast they forgot the documentation.",
  "{companyName}'s code review turned into a therapy session. Again.",
  "{companyName}'s developers broke production. Team lunch is canceled.",
  
  // General tech humor
  "A developer walked into a bar. They ordered 1.9999999999999999 beers.",
  "Why do programmers prefer dark mode? Because light attracts bugs!",
  "There are only 10 types of people: those who understand binary and those who don't.",
  "A SQL query walks into a bar, approaches two tables and asks: 'Mind if I join you?'",
  "Why was the JavaScript developer sad? Because he didn't Node how to Express himself.",
  "In case of fire: git commit, git push, leave the building.",
  "99 little bugs in the code, 99 little bugs. Take one down, patch it around... 127 little bugs in the code.",
  "Debugging is like being a detective in a crime movie where you're also the murderer.",
  "A QA engineer walks into a bar. Orders 1 beer. Orders 0 beers. Orders 999999 beers. Orders -1 beers. Orders a lizard.",
  "I don't always test my code, but when I do, I do it in production.",
  
  // More company puns
  "{companyName} just discovered their bug tracking system has bugs.",
  "{companyName}'s tech debt is now its own department.",
  "Breaking: {companyName} decides to 'move fast and fix things later'. Later never came.",
  "{companyName}'s sprint retrospective summary: 'We should have fewer meetings.'",
  "{companyName} achieved 100% code coverage. The tests don't pass, but they exist!",
  "{companyName}'s interns just pushed to main. Prayers welcome.",
  "{companyName} is hiring: 'Looking for a developer with 10 years of experience in a 5-year-old framework.'",
  "{companyName}'s Slack is 90% memes, 10% actual work. Productivity is at an all-time high.",
  "{companyName}'s microservices are communicating. Unfortunately, they're plotting revenge.",
  "{companyName} successfully migrated to the cloud. Now they can't find anything.",
  
  // More general tech humor
  "To understand recursion, you must first understand recursion.",
  "A programmer's wife tells him: 'Go to the store and buy milk. If they have eggs, get a dozen.' He returns with 12 cartons of milk.",
  "The best thing about a Boolean is that even if you're wrong, you're only off by a bit.",
  "Why do Java developers wear glasses? Because they can't C#.",
  "How many programmers does it take to change a light bulb? None. It's a hardware problem.",
  "Algorithm: word used by programmers when they don't want to explain what they did.",
  "There's no place like 127.0.0.1",
  "I would tell you a UDP joke, but you might not get it.",
  "Real programmers count from 0.",
  "!false - It's funny because it's true.",
];

// All activity categories
const allActivityCategories: ActivityGenerator[][] = [
  sprintActivities,
  storiesActivities,
  epicCompletedActivities,
  epicProgressActivities,
  customerSignupActivities,
  customerNameActivities,
  bankBalanceActivities,
  employeesFiredActivities,
  storyPointsThisSprintActivities,
  sprintPercentageActivities,
  epicFocusActivities,
  versionActivities,
];

/**
 * Helper function to get ordinal suffix for a number
 */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  // Handle 11, 12, 13 (teens) which all use 'th'
  if (v >= 11 && v <= 13) {
    return n + 'th';
  }
  return n + (s[v % 10] || s[0]);
}

/**
 * Helper function to get a random item from an array
 */
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Generate activities based on the current game state
 * Returns at least 2 activities, possibly with a bonus funny activity (50% chance)
 */
export function generateActivities(state: GameState): string[] {
  const activities: string[] = [];
  const usedCategories: Set<ActivityGenerator[]> = new Set();

  // Keep trying to generate activities until we have at least 2
  let attempts = 0;
  const maxAttempts = 100;

  while (activities.length < 2 && attempts < maxAttempts) {
    // Get a random category that hasn't been used
    const availableCategories = allActivityCategories.filter(cat => !usedCategories.has(cat));
    
    if (availableCategories.length === 0) {
      // All categories exhausted, start over
      usedCategories.clear();
      continue;
    }

    const category = getRandomItem(availableCategories);
    usedCategories.add(category);

    // Get a random generator from the category
    const generator = getRandomItem(category);
    const activity = generator(state);

    if (activity) {
      activities.push(activity);
    }

    attempts++;
  }

  // 50/50 roll for bonus funny activity
  if (Math.random() < 0.5) {
    const funnyActivity = getRandomItem(funnyActivities);
    // Replace {companyName} placeholder if present
    activities.push(funnyActivity.replace(/{companyName}/g, state.companyName));
  }

  return activities;
}

/**
 * Generate a single random activity for display
 * This picks one from the generated activities
 */
export function generateSingleActivity(state: GameState): string {
  const activities = generateActivities(state);
  return activities.length > 0 ? getRandomItem(activities) : `${state.companyName} is making progress!`;
}
